import type { ApiResponseError } from '@centric-os/types';
import type { ServiceClient } from '@compassdigital/sdk.typescript';
import type { AxiosInstance } from 'axios';
import type { Store, StoreActions } from 'pinia';
import { markRaw, reactive, toRefs } from 'vue';

type PluginOptions = {
  cdlApi?: AxiosInstance;
  genericAPI?: AxiosInstance;
  serviceApi?: ServiceClient;
};

type ActionState = {
  loading: boolean;
  ready: boolean;
  errors: ApiResponseError | null;
  pendingCalls: number;
};

export const APIPlugin = ({ cdlApi, genericAPI, serviceApi }: PluginOptions) => {
  return () => ({
    cdlApi: cdlApi && markRaw(cdlApi),
    genericAPI: genericAPI && markRaw(genericAPI),
    serviceApi: serviceApi && markRaw(serviceApi),
  });
};

export function useStoreAPIAction<S extends Store, AC extends keyof StoreActions<S>>(
  store: S,
  actionName: AC,
) {
  const state = reactive<ActionState>({
    loading: false,
    ready: false,
    errors: null,
    pendingCalls: 0,
  });
  store.$onAction(({ after, name, onError }) => {
    if (name === actionName) {
      state.pendingCalls++;

      state.loading = true;
      state.ready = false;
      state.errors = null;
      after(() => {
        state.pendingCalls--;

        state.loading = state.pendingCalls > 0;
        state.ready = state.pendingCalls === 0;
      });

      onError((errors) => {
        state.pendingCalls--;

        state.errors = errors;
        state.loading = state.pendingCalls > 0;
        state.ready = state.pendingCalls === 0;
      });
    }
  });

  return { ...toRefs(state) };
}
